import { Component, OnDestroy, OnInit } from '@angular/core';
import { AppState } from 'src/app/core/app.state';
import { Store } from '@ngrx/store';
import { FetchLocations, SetCompanyFilter } from 'src/app/features/customers/store/customers.actions';
import { ICompanyFilter, ILocation } from 'src/app/features/customers/models/customers.models';
import { Observable, Subscription, combineLatest, debounceTime, distinctUntilChanged, map, tap } from 'rxjs';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';
import { ICompaniesGroups } from '../../models/companies-groups.model';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AddCompaniesGroupe, AddCompaniesGroupeSuccess, EditCompaniesGroupe, EditCompaniesGroupeSuccess, GetCompaniesGroupeById, GetCompaniesGroupeByIdSuccess } from '../../store/companies-groups.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { SetCurrentElementId } from 'src/app/shared/store/shared.actions';
import { MatDialog } from '@angular/material/dialog';
import { CustomersFiltersComponent } from 'src/app/features/customers/components/customers-filters/customers-filters.component';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';
import { parseCompanyFilterRequest, refillCompanyFilter } from 'src/app/shared/utils/helpers';

@Component({
  selector: 'app-add-companies-groups',
  templateUrl: './add-companies-groups.component.html',
  styleUrls: ['./add-companies-groups.component.scss'],
})
export class AddCompaniesGroupsComponent implements OnInit, OnDestroy {
  id!: number;
  formAddCompaniesGroups: FormGroup = new FormGroup({});

  readonly canCreate$ = this.store.select(selectCanDo('company', 'create'));
  readonly canEdit$ = this.store.select(selectCanDo('company', 'edit'));

  get canModify$(): Observable<boolean> {
    return this.id ? this.canEdit$ : this.canCreate$;
  }
  pagination$: Observable<IPagination | null> = this.store.select(
    (state) => state.customers.metadata
  );
  locations$: Observable<ILocation[]> = this.store.select(
    (state) => state.customers.locations
  );
  companiesGroup$: Observable<ICompaniesGroups | null> =
    this.store.select((state) => state.companiesGroups.companiesGroup);

  selectedLocations: ILocation[] = [];
  CompanieGroupe!: ICompaniesGroups;

  locations: ILocation[] = [];
  private subscriptions: Subscription[] = [];

  search = new FormControl('');
  selectedSearch = new FormControl('');

  filter: any | undefined = undefined;
  unparsedFilter: any | undefined = undefined;
  key: string | undefined = undefined;
  page: IPageCount = {
    size: DEFAULT_PAGE_SIZE,
    number: 1,
  };

  // Per-pane checkbox selection state for bulk add / remove
  availableSelectedIds: number[] = [];
  selectedSelectedIds: number[] = [];

  selectedFilterKey = '';

  constructor(
    private store: Store<AppState>,
    private fb: FormBuilder,
    private routerActive: ActivatedRoute,
    private actions$: Actions,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.id = +this.routerActive.snapshot.paramMap.get('id')!;
    if (this.id) {
      this.store.dispatch(SetCurrentElementId({ currentElementId: this.id }));
      this.store.dispatch(GetCompaniesGroupeById({ id: this.id }));
    }

    const sub2 = this.locations$.subscribe((locations) => {
      this.locations = [...locations];
      // Drop available-pane selection IDs that aren't on the page anymore
      const ids = this.locations.map((l) => l.id);
      this.availableSelectedIds = this.availableSelectedIds.filter((id) => ids.includes(id));
    });

    const locationStateSub = this.store
      .select((state) => state.customers.companyFilterState)
      .subscribe((filter) => {
        this.key = filter.keyword;
        this.unparsedFilter = filter.advanced;
        this.filter = parseCompanyFilterRequest(filter.advanced);
        this.toPage(this.page);
      });

    const sub1 = this.actions$
      .pipe(
        ofType(GetCompaniesGroupeByIdSuccess),
        tap((action) => {
          this.CompanieGroupe = action.companiesGroup;
          if (
            action.companiesGroup.locations &&
            action.companiesGroup.locations.length > 0
          ) {
            this.selectedLocations = action.companiesGroup.locations.map(
              (location: any) => ({ ...location })
            );
          }
          this.initializeForm();
        })
      )
      .subscribe();

    const sub3 = this.actions$
      .pipe(
        ofType(AddCompaniesGroupeSuccess, EditCompaniesGroupeSuccess),
        tap(() => {
          this.router.navigate(['companies-groups']);
        })
      )
      .subscribe();

    const sub4 = this.search.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.key = searchTerm ?? '';
        this.store.dispatch(
          SetCompanyFilter({ keyword: this.key, advanced: this.unparsedFilter })
        );
      });

    const sub5 = this.selectedSearch.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe((value) => {
        this.selectedFilterKey = (value ?? '').toString().toLowerCase();
      });

    this.subscriptions.push(sub1, sub2, sub3, sub4, sub5, locationStateSub);
  }

  private initializeForm(): void {
    this.formAddCompaniesGroups = this.fb.group({
      name: new FormControl(this.CompanieGroupe?.name ?? '', Validators.required),
      description: new FormControl(this.CompanieGroupe?.description ?? ''),
      image: new FormControl(this.CompanieGroupe?.image),
    });
  }

  // ── Display helpers ─────────────────────────────────────────────────────

  trackByFn(_: number, item: ILocation): number {
    return item.id as number;
  }

  isSelectedLocation(location: ILocation): boolean {
    return this.selectedLocations.some((l) => l.id === location.id);
  }

  get filteredSelectedLocations(): ILocation[] {
    if (!this.selectedFilterKey) return this.selectedLocations;
    return this.selectedLocations.filter((l) =>
      `${l.company_name ?? ''} ${l.name ?? ''} ${l.apartment ?? ''}`
        .toLowerCase()
        .includes(this.selectedFilterKey)
    );
  }

  // ── Available pane checkbox selection ───────────────────────────────────

  isAvailableSelected(id: number | undefined): boolean {
    return id != null && this.availableSelectedIds.includes(id);
  }

  toggleAvailable(id: number | undefined, checked: boolean): void {
    if (id == null) return;
    if (checked) {
      if (!this.availableSelectedIds.includes(id))
        this.availableSelectedIds = [...this.availableSelectedIds, id];
    } else {
      this.availableSelectedIds = this.availableSelectedIds.filter((x) => x !== id);
    }
  }

  get availableSelectableIds(): number[] {
    return this.locations
      .filter((l) => !this.isSelectedLocation(l) && l.id != null)
      .map((l) => l.id as number);
  }

  get isAllAvailableSelected(): boolean {
    const ids = this.availableSelectableIds;
    return ids.length > 0 && ids.every((id) => this.availableSelectedIds.includes(id));
  }

  get isPartialAvailableSelected(): boolean {
    return this.availableSelectedIds.length > 0 && !this.isAllAvailableSelected;
  }

  toggleAllAvailable(checked: boolean): void {
    this.availableSelectedIds = checked ? [...this.availableSelectableIds] : [];
  }

  addSelectedFromAvailable(): void {
    if (this.availableSelectedIds.length === 0) return;
    const toAdd = this.locations.filter((l) => l.id != null && this.availableSelectedIds.includes(l.id));
    toAdd.forEach((loc) => {
      if (!this.isSelectedLocation(loc)) this.selectedLocations.push(loc);
    });
    this.availableSelectedIds = [];
  }

  // ── Selected pane checkbox selection ────────────────────────────────────

  isSelectedRowSelected(id: number | undefined): boolean {
    return id != null && this.selectedSelectedIds.includes(id);
  }

  toggleSelectedRow(id: number | undefined, checked: boolean): void {
    if (id == null) return;
    if (checked) {
      if (!this.selectedSelectedIds.includes(id))
        this.selectedSelectedIds = [...this.selectedSelectedIds, id];
    } else {
      this.selectedSelectedIds = this.selectedSelectedIds.filter((x) => x !== id);
    }
  }

  get isAllSelectedSelected(): boolean {
    const ids = this.filteredSelectedLocations
      .filter((l) => l.id != null)
      .map((l) => l.id as number);
    return ids.length > 0 && ids.every((id) => this.selectedSelectedIds.includes(id));
  }

  get isPartialSelectedSelected(): boolean {
    return this.selectedSelectedIds.length > 0 && !this.isAllSelectedSelected;
  }

  toggleAllSelected(checked: boolean): void {
    this.selectedSelectedIds = checked
      ? this.filteredSelectedLocations
          .filter((l) => l.id != null)
          .map((l) => l.id as number)
      : [];
  }

  removeSelectedFromGroup(): void {
    if (this.selectedSelectedIds.length === 0) return;
    this.selectedLocations = this.selectedLocations.filter(
      (l) => l.id == null || !this.selectedSelectedIds.includes(l.id)
    );
    this.selectedSelectedIds = [];
  }

  // ── Filter dialog (reuses CustomersFiltersComponent unchanged) ──────────

  openFilterDialog(): void {
    const dialogRef = this.dialog.open(CustomersFiltersComponent, {
      minWidth: '650px',
      width: '80vw',
      height: '80vh',
    });

    dialogRef.afterClosed().subscribe((filter: ICompanyFilter) => {
      if (filter) {
        this.filter = filter;
        this.page = { ...this.page, number: 1 };
        this.store.dispatch(
          SetCompanyFilter({ keyword: this.search.value ?? '', advanced: filter })
        );
      }
    });
  }

  hasAvailableFilters(): boolean {
    return !!(this.filter && this.filter.filters && this.filter.filters.length > 0);
  }

  clearAvailableFilters(): void {
    this.page = { ...this.page, number: 1 };
    this.store.dispatch(
      SetCompanyFilter({ keyword: this.search.value ?? '', advanced: refillCompanyFilter() })
    );
  }

  toPage(page: IPageCount): void {
    this.page = { ...this.page, number: page.number, size: page.size };
    this.store.dispatch(
      FetchLocations({ page: this.page, filter: this.filter, key: this.key })
    );
  }

  // ── Save / Cancel ───────────────────────────────────────────────────────

  cancel(): void {
    this.router.navigate(['companies-groups'], {
      queryParamsHandling: 'merge',
    });
  }

  save(): void | boolean {
    if (this.formAddCompaniesGroups.invalid) {
      this.formAddCompaniesGroups.markAllAsTouched();
      return false;
    }
    const payload: Omit<ICompaniesGroups, 'id'> = {
      name: this.formAddCompaniesGroups?.get('name')?.value,
      description: this.formAddCompaniesGroups?.get('description')?.value,
      image: this.formAddCompaniesGroups?.get('image')?.value,
      locations_ids: this.selectedLocations.map((l) => l.id),
    };

    if (this.id) {
      this.store.dispatch(
        EditCompaniesGroupe({ id: this.CompanieGroupe.id, payload })
      );
      return;
    }
    this.store.dispatch(AddCompaniesGroupe({ payload }));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.store.dispatch(SetCompanyFilter({ keyword: '', advanced: refillCompanyFilter() }));
  }
}
